import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';
import {
  BbFeatureFlag,
  bbGetFeatureFlags,
  bbSetFeatureFlag,
} from '../../../services/blindBoxService';

/** 与设计/运营口径一致的人类可读名；未知 key 仍回退为 key */
const FLAG_TITLE_I18N: Record<string, string> = {
  blind_box_enabled: 'module.blindBox.flagTitle_blind_box_enabled',
  wheel_enabled: 'module.blindBox.flagTitle_wheel_enabled',
  lottery_enabled: 'module.blindBox.flagTitle_lottery_enabled',
  task_center_enabled: 'module.blindBox.flagTitle_task_center_enabled',
  withdrawal_enabled: 'module.blindBox.flagTitle_withdrawal_enabled',
  leaderboard_enabled: 'module.blindBox.flagTitle_leaderboard_enabled',
};

const FLAG_DESC_I18N: Record<string, string> = {
  blind_box_enabled: 'module.blindBox.flagDesc_blind_box_enabled',
  wheel_enabled: 'module.blindBox.flagDesc_wheel_enabled',
  lottery_enabled: 'module.blindBox.flagDesc_lottery_enabled',
  task_center_enabled: 'module.blindBox.flagDesc_task_center_enabled',
  withdrawal_enabled: 'module.blindBox.flagDesc_withdrawal_enabled',
  leaderboard_enabled: 'module.blindBox.flagDesc_leaderboard_enabled',
};

const FLAG_ORDER = [
  'blind_box_enabled',
  'wheel_enabled',
  'lottery_enabled',
  'task_center_enabled',
  'withdrawal_enabled',
] as const;

export function SectionFeatureFlags({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // POST /blind-box/feature-flags requires
  // bb-leaderboard:update OR bb-activity-basic:update OR bb-holiday-lottery:update
  const canToggleFlag =
    hasPermission(permissionList, 'bb-leaderboard', 'update') ||
    hasPermission(permissionList, 'bb-activity-basic', 'update') ||
    hasPermission(permissionList, 'bb-holiday-lottery', 'update');
  const [flags, setFlags] = useState<BbFeatureFlag[]>([]);

  const load = useCallback(() => {
    bbGetFeatureFlags()
      .then(setFlags)
      .catch(() => setFlags([]));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const sortedFlags = useMemo(() => {
    const byKey = new Map(flags.map((f) => [f.key, f]));
    const seen = new Set<string>();
    const ordered: BbFeatureFlag[] = [];
    FLAG_ORDER.forEach((k) => {
      const row = byKey.get(k);
      if (row) {
        ordered.push(row);
        seen.add(k);
      }
    });
    flags.forEach((f) => {
      if (!seen.has(f.key)) ordered.push(f);
    });
    return ordered;
  }, [flags]);

  const toggleFlag = async (key: string, value: boolean) => {
    try {
      await bbSetFeatureFlag(key, value);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{t('module.blindBox.featureFlags')}</Typography>
      {sortedFlags.map((f) => {
        const titleKey = FLAG_TITLE_I18N[f.key];
        const humanTitle = titleKey ? t(titleKey) : f.key;
        const descKey = FLAG_DESC_I18N[f.key];
        const humanDesc = descKey ? t(descKey, { _: '' }) : '';
        return (
          <Stack
            key={f.key}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <FormControlLabel
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={!!f.value}
                  disabled={!canToggleFlag}
                  onChange={(e) => toggleFlag(f.key, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{humanTitle}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {/*{f.key}*/}
                    {/*{f.description ? ` — ${f.description}` : ''}*/}
                    {/*{humanDesc ? ` — ${humanDesc}` : f.description ? ` — ${f.description}` : ''}*/}
                    {`${humanDesc}`}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        );
      })}
    </Stack>
  );
}
